import { useState } from "react";
import { auditWebsiteUrlSchema, normalizeAuditWebsiteUrl } from "@/lib/auditUrl";
import { trackFormStart, trackFormSubmit, trackScoreFlowEvent } from "@/lib/analytics";
import type { ScoreNextIntent, ScoreSourcePlacement } from "@/lib/scoreJourney";

type Placement =
  | "hero"
  | "blog_end"
  | "service_hero"
  | "service_end"
  | "header"
  | "sticky_mobile"
  | "citation_hero"
  | "citation_end"
  | "ecommerce_hero"
  | "ecommerce_end";

const SOURCE_BY_PLACEMENT: Record<Placement, ScoreSourcePlacement> = {
  hero: "home_hero",
  blog_end: "blog_end",
  service_hero: "service_hero",
  service_end: "service_end",
  header: "header",
  sticky_mobile: "sticky_mobile",
  citation_hero: "citation_hero",
  citation_end: "citation_end",
  ecommerce_hero: "ecommerce_hero",
  ecommerce_end: "ecommerce_end",
};

const SERVICE_SCORE_PLACEMENTS = new Set<Placement>([
  "citation_hero",
  "citation_end",
  "ecommerce_hero",
  "ecommerce_end",
]);

function getVariant(placement: Placement): {
  label: string;
  placeholder: string;
  leadIn?: string;
  microcopy: string;
  nextIntent: ScoreNextIntent;
  buttonLabel: string;
} {
  if (placement === "citation_hero" || placement === "citation_end") {
    return {
      label: "Your website URL",
      placeholder: "yourbusiness.co.uk",
      leadIn:
        placement === "citation_hero"
          ? "Step 1: see where your site stands"
          : undefined,
      microcopy:
        "Your free 0–100 score for how ready your site is for AI answers. No card needed.",
      nextIntent: "citation",
      buttonLabel: "Check my AI visibility",
    };
  }

  if (placement === "ecommerce_hero" || placement === "ecommerce_end") {
    return {
      label: "Your website URL",
      placeholder: "yourstore.co.uk",
      microcopy:
        "Free AI Visibility Score: your 0–100 score for how ready your store is for AI answers, plus the areas to review first. No card needed.",
      nextIntent: "none",
      buttonLabel: "Check my AI visibility",
    };
  }

  return {
    label: "Your website",
    placeholder: "yourbusiness.co.uk",
    microcopy:
      "Free AI Visibility Score: a 0–100 readiness baseline for your own website, plus the areas to review first. No card needed. We use the website address you enter to run your score, and your email to send your code and report.",
    nextIntent: "none",
    buttonLabel: "Check my AI visibility",
  };
}

export default function ScoreInlineForm({
  placement,
  compact = false,
}: {
  placement: Placement;
  compact?: boolean;
}) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const source = SOURCE_BY_PLACEMENT[placement];
  const variant = getVariant(placement);
  const isServiceVariant = SERVICE_SCORE_PLACEMENTS.has(placement);
  const fieldId = `score-url-${placement}`;
  const errorId = `${fieldId}-error`;
  const microcopyId = `${fieldId}-microline`;

  function scoreEventContext() {
    return {
      placement: source,
      next_intent: variant.nextIntent,
      ...(isServiceVariant ? { page_type: "service" as const } : {}),
      ...(placement.startsWith("citation_")
        ? { niche: "ai_citation_programme" as const }
        : {}),
    };
  }

  function markStarted() {
    if (started) return;
    setStarted(true);
    if (isServiceVariant) trackScoreFlowEvent("score_start", scoreEventContext());
    else trackFormStart({ form_name: "ai_visibility_score_url", form_location: placement });
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    const normalized = normalizeAuditWebsiteUrl(url);
    if (!normalized.ok || !auditWebsiteUrlSchema.safeParse(normalized.value).success) {
      setError(
        url.trim()
          ? "That doesn't look like a website address. Try yourbusiness.co.uk."
          : "Enter your website address, e.g. yourbusiness.co.uk."
      );
      return;
    }
    setSubmitting(true);
    if (isServiceVariant) {
      trackScoreFlowEvent("lead_form_submit", {
        form_id: "score",
        ...scoreEventContext(),
      });
    } else {
      trackFormSubmit({ form_name: "ai_visibility_score_url", form_location: placement });
    }
    const next = variant.nextIntent === "citation" ? "&next=citation" : "";
    window.setTimeout(() => {
      window.location.assign(
        `/free-ai-growth-audit/?url=${encodeURIComponent(normalized.value)}&src=${source}${next}`
      );
    }, 0);
  }

  return (
    <form
      onSubmit={submit}
      method="get"
      action="/free-ai-growth-audit/"
      className={
        compact
          ? "ym-hide-content ym-disable-keys ym-disable-submit score-inline flex min-w-0 items-end gap-2"
          : "ym-hide-content ym-disable-keys ym-disable-submit score-inline rounded-xl border border-[#00C9A7]/35 bg-[#0F1419] p-4 shadow-sm"
      }
      noValidate
    >
      <input type="hidden" name="src" value={source} />
      {variant.nextIntent === "citation" && (
        <input type="hidden" name="next" value="citation" />
      )}
      {!compact && !isServiceVariant && (
        <p className="mb-3 text-sm font-semibold text-white">
          Free AI Visibility Score
        </p>
      )}
      {variant.leadIn && (
        <p className="mb-3 text-sm font-semibold text-white">{variant.leadIn}</p>
      )}
      <div
        className={
          compact
            ? "flex min-w-0 flex-1 items-end gap-2"
            : "flex min-h-[112px] flex-col gap-2 sm:min-h-[48px] sm:flex-row sm:items-end"
        }
      >
        <div className="min-w-0 flex-1">
          <label className="mb-1 block text-xs font-semibold text-white" htmlFor={fieldId}>
            {variant.label}
          </label>
          <input
            id={fieldId}
            name="url"
            type="text"
            inputMode="url"
            autoComplete="url"
            autoCapitalize="off"
            spellCheck={false}
            value={url}
            onFocus={markStarted}
            onChange={(event) => {
              markStarted();
              setUrl(event.target.value);
              if (error) setError("");
            }}
            onBlur={() => {
              if (url && !normalizeAuditWebsiteUrl(url).ok)
                setError("That doesn't look like a website address. Try yourbusiness.co.uk.");
            }}
            placeholder={variant.placeholder}
            className={
              compact
                ? "min-w-0 w-full rounded-sm border border-white/30 bg-white px-3 py-2 text-sm text-[#0F1419] outline-none focus-visible:ring-2 focus-visible:ring-[#00D4AA]"
                : "min-h-12 min-w-0 w-full rounded-sm border border-white/20 bg-white px-4 py-3 text-base text-[#0F1419] outline-none focus-visible:ring-2 focus-visible:ring-[#00D4AA]"
            }
            aria-invalid={Boolean(error)}
            aria-describedby={`${microcopyId} ${errorId}`}
            required
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className={
            compact
              ? "min-h-10 shrink-0 rounded-sm bg-[#FF8C00] px-3 py-2 text-sm font-bold text-[#0F1419] disabled:opacity-70"
              : "min-h-12 shrink-0 rounded-sm bg-[#FF8C00] px-5 py-3 text-base font-bold text-[#0F1419] transition hover:bg-[#E67E00] disabled:opacity-70"
          }
        >
          {submitting ? "Checking…" : variant.buttonLabel}
        </button>
      </div>
      <p id={errorId} className="mt-2 min-h-5 text-xs text-[#ff9f9f]" aria-live="polite">
        {error}
      </p>
      {!compact && (
        <p id={microcopyId} className="text-xs leading-relaxed text-white/60">
          {variant.microcopy}{" "}
          {!isServiceVariant && (
            <a href="/privacy-policy/" className="underline underline-offset-2 hover:text-white">
              Privacy policy
            </a>
          )}
        </p>
      )}
    </form>
  );
}
